import { itemsApi } from "#/api/itemsApi";
import BaseTable, { type TableSortProps } from "#/components/tables/BaseTable";
import Pagination from "#/components/buttons/Pagination";
import ExportConfirmationModal from "#/components/overlays/ExportConfirmationModal";
import type { Item } from "#/types/items";
import type { ScanHistory } from "#/types/scan-history";
import { ScanHistoryType } from "#/types/scan-history";
import { sanitize } from "#/utils/StringUtils";
import { getHardwareLabel } from "#/utils/deviceHardware";
import { getItemStatusColor } from "#/utils/ColorUtils";
import { exportToCSV } from "#/utils/CsvUtils";
import { logger } from "#/utils/logger";
import { Anchor, Badge, Button, CloseButton, Table } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconDownload, IconRefresh } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useClient } from "#/hook/useClient";
import { formatDate, formatDateTimeForExport, formatTime } from "#/utils/dateTimeUtils";
import moment from "moment";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

export interface Props {
  item?: Item;
  type: "refill" | "scan";
  /** Rendered at the start of the header row (title, scan/refill switch). */
  headerStart?: ReactNode;
  /** When given, a close button ends the header row after the table controls. */
  onClose?: () => void;
}
const HistoricalDataTable = ({ item, type, headerStart, onClose }: Props) => {
  const { t } = useTranslation();
  const { client } = useClient();
  const isRpi = type === "refill";
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(25);
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");

  const [exportModalOpened, { open: openExportModal, close: closeExportModal }] =
    useDisclosure(false);

  const headers = isRpi
    ? [
        { key: "createdAt", value: "Date" },
        { key: "device", value: "RFID Scanner" },
        { key: "initial_weight", value: "Initial Weight" },
        { key: "gross_weight", value: "Gross Weight" },
        { key: "net_weight", value: "Net Weight" },
        { key: "tare_weight", value: "Tare Weight" },
        { key: "gas_added_weight", value: "Gas Added Weight" },
        { key: "refill_success", value: "Refill Success" },
      ]
    : [
        { key: "createdAt", value: "Date" },
        { key: "device", value: "RFID Scanner" },
        { key: "type", value: "Type" },
        { key: "status_label", value: "New Values" },
        { key: "location", value: "Location" },
      ];
  const [sortBy, setSortBy] = useState<string>("id");

  const {
    data: { items = [], total = 0, per_page = 0 } = {},
    isFetching,
    refetch,
  } = useQuery({
    queryKey: [
      `item-${item?.id}-${type}-historical-data`,
      item,
      sortDirection,
      sortBy,
      page,
      limit,
    ],
    queryFn: async () => {
      if (!item || !type) {
        return {
          items: [],
          total: 0,
          per_page: 0,
        };
      }
      const response = await itemsApi.getHistory(item?.id, type, {
        page: page,
        limit: limit,
        sortBy: sortBy,
        order: sortDirection,
      });
      if (!response.data) {
        throw new Error("Failed to fetch history");
      }
      return {
        items: response.data.data,
        total: response.data.total,
        per_page: response.data.limit,
      };
    },
    enabled: type && item != undefined && item.id > 0,
    staleTime: 1000 * 60,
  });

  const handleExportCurrentPage = () => {
    const exportData = items.map((historyItem: ScanHistory) => ({
      date: formatDateTimeForExport(historyItem.createdAt),
      device_name: historyItem.device?.name ?? "",
      device_type: getHardwareLabel(historyItem.device?.hardware_type, ""),
      ...(isRpi
        ? {
            initial_weight: historyItem.initial_weight ?? "",
            gross_weight: historyItem.gross_weight ?? "",
            net_weight: historyItem.net_weight ?? "",
            tare_weight: historyItem.tare_weight ?? "",
            gas_added_weight: historyItem.gas_added_weight ?? "",
            refill_success: historyItem.refill_success ? "Yes" : "No",
            refill_error_reason: historyItem.refill_error_reason ?? "",
          }
        : {
            type: historyItem.type ?? "auto",
            status_label: historyItem.status_label ?? "",
            sub_client: historyItem.sub_client_name ?? "",
            location: historyItem.location ?? "",
            latitude: historyItem.latitude ?? "",
            longitude: historyItem.longitude ?? "",
          }),
    }));

    const csvHeaders = isRpi
      ? [
          "date",
          "device_name",
          "device_type",
          "initial_weight",
          "gross_weight",
          "net_weight",
          "tare_weight",
          "gas_added_weight",
          "refill_success",
          "refill_error_reason",
        ]
      : [
          "date",
          "device_name",
          "device_type",
          "type",
          "status_label",
          "sub_client",
          "location",
          "latitude",
          "longitude",
        ];

    const headerLabels = isRpi
      ? {
          date: "Date",
          device_name: "Device Name",
          device_type: "Device Type",
          initial_weight: "Initial Weight",
          gross_weight: "Gross Weight",
          net_weight: "Net Weight",
          tare_weight: "Tare Weight",
          gas_added_weight: "Gas Added Weight",
          refill_success: "Refill Success",
          refill_error_reason: "Refill Error Reason",
        }
      : {
          date: "Date",
          device_name: "Device Name",
          device_type: "Device Type",
          type: "Type",
          status_label: "New Status",
          sub_client: "New Sub-Client",
          location: "Location",
          latitude: "Latitude",
          longitude: "Longitude",
        };

    const note =
      "NOTE: All dates and times in this export are in UTC (Coordinated Universal Time) format.\nFormat: DD/MM/YYYY HH:mm (Day/Month/Year Hour:Minute in 24-hour format)";
    const filename = `item-${item?.id}-history-${moment().format("YYYY-MM-DD-HHmmss")}.csv`;
    exportToCSV(exportData, csvHeaders, filename, headerLabels, note);
  };

  const handleExportAll = async () => {
    try {
      if (!item || !type) return;

      const response = await itemsApi.getHistory(item.id, type, {
        page: 1,
        limit: total,
        sortBy: sortBy,
        order: sortDirection,
      });

      const allItems = response.data.data;

      const exportData = allItems.map((historyItem: ScanHistory) => ({
        date: formatDateTimeForExport(historyItem.createdAt),
        device_name: historyItem.device?.name ?? "",
        device_type: getHardwareLabel(historyItem.device?.hardware_type, ""),
        ...(isRpi
          ? {
              initial_weight: historyItem.initial_weight ?? "",
              gross_weight: historyItem.gross_weight ?? "",
              net_weight: historyItem.net_weight ?? "",
              tare_weight: historyItem.tare_weight ?? "",
              gas_added_weight: historyItem.gas_added_weight ?? "",
              refill_success: historyItem.refill_success ? "Yes" : "No",
              refill_error_reason: historyItem.refill_error_reason ?? "",
            }
          : {
              type: historyItem.type ?? "auto",
              status_label: historyItem.status_label ?? "",
              sub_client: historyItem.sub_client_name ?? "",
              location: historyItem.location ?? "",
              latitude: historyItem.latitude ?? "",
              longitude: historyItem.longitude ?? "",
            }),
      }));

      const csvHeaders = isRpi
        ? [
            "date",
            "device_name",
            "device_type",
            "initial_weight",
            "gross_weight",
            "net_weight",
            "tare_weight",
            "gas_added_weight",
            "refill_success",
            "refill_error_reason",
          ]
        : [
            "date",
            "device_name",
            "device_type",
            "type",
            "status_label",
            "sub_client",
            "location",
            "latitude",
            "longitude",
          ];

      const headerLabels = isRpi
        ? {
            date: "Date",
            device_name: "Device Name",
            device_type: "Device Type",
            initial_weight: "Initial Weight",
            gross_weight: "Gross Weight",
            net_weight: "Net Weight",
            tare_weight: "Tare Weight",
            gas_added_weight: "Gas Added Weight",
            refill_success: "Refill Success",
            refill_error_reason: "Refill Error Reason",
          }
        : {
            date: "Date",
            device_name: "Device Name",
            device_type: "Device Type",
            type: "Type",
            status_label: "New Status",
            sub_client: "New Sub-Client",
            location: "Location",
            latitude: "Latitude",
            longitude: "Longitude",
          };

      const note =
        "NOTE: All dates and times in this export are in UTC (Coordinated Universal Time) format.\nFormat: DD/MM/YYYY HH:mm (Day/Month/Year Hour:Minute in 24-hour format)";
      const filename = `item-${item?.id}-history-all-${moment().format("YYYY-MM-DD-HHmmss")}.csv`;
      exportToCSV(exportData, csvHeaders, filename, headerLabels, note);
    } catch (error) {
      logger.error("Failed to export all history:", error);
    }
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <div className="mb-4 flex w-full items-center justify-between gap-4 border-b border-b-slate-300 pb-4">
        <div className="flex items-center gap-2">{headerStart}</div>
        <div className="flex items-center gap-3">
          <Pagination
            page={page}
            total={total}
            limit={per_page}
            onChange={setPage}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setPage(1);
            }}
          />
          <div className="h-8 w-px bg-gray-300" />
          <Button
            leftSection={<IconDownload size={18} />}
            variant="outline"
            onClick={openExportModal}
            disabled={!items || items.length === 0}
          >
            {t("common.exportCsv")}
          </Button>
          <Button
            leftSection={<IconRefresh size={18} />}
            variant="outline"
            color="green"
            onClick={() => refetch()}
            loading={isFetching}
          >
            {t("common.refresh")}
          </Button>
          {onClose && (
            <>
              <div className="h-8 w-px bg-gray-300" />
              <CloseButton onClick={onClose} />
            </>
          )}
        </div>
      </div>
      <div className="min-h-0 w-full flex-1 overflow-auto">
        <BaseTable
          headers={headers.map((item) => item.value)}
          loading={isFetching}
          allowSorting={true}
          sortableColumns={[0, 1]}
          onSortChange={(e: TableSortProps) => {
            setPage(1);
            setSortDirection(e.direction);
            setSortBy(headers[e.index].key);
          }}
          defaultSort={{
            direction: "DESC",
            index: 0,
          }}
          rows={items.map((item) => {
            return (
              <Table.Tr key={item.id}>
                <Table.Td>
                  {item.createdAt ? (
                    <span>
                      {formatDate(item.createdAt, client)}
                      <br />
                      {formatTime(item.createdAt, client)}
                    </span>
                  ) : (
                    "--"
                  )}
                </Table.Td>
                <Table.Td>
                  <div className="flex flex-col items-start gap-1">
                    <div className="flex flex-col">
                      <span>{item.device?.name ?? "--"}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold uppercase">
                          {getHardwareLabel(item.device?.hardware_type)}
                        </span>
                      </div>
                    </div>
                    {item.device?.id && (
                      <Anchor
                        size="xs"
                        onClick={() => window.open(`/devices/${item.device?.id}`, "_blank")}
                        className="cursor-pointer"
                        underline="always"
                      >
                        View
                      </Anchor>
                    )}
                  </div>
                </Table.Td>
                {isRpi ? (
                  <>
                    <Table.Td>{item.initial_weight ?? "--"}</Table.Td>
                    <Table.Td>{item.gross_weight ?? "--"}</Table.Td>
                    <Table.Td>{item.net_weight ?? "--"}</Table.Td>
                    <Table.Td>{item.tare_weight ?? "--"}</Table.Td>
                    <Table.Td>{item.gas_added_weight ?? "--"}</Table.Td>
                    <Table.Td>
                      <div className="flex w-full flex-col">
                        <span>{item.refill_success ? "Yes" : "No"}</span>
                        {item.refill_error_reason && <span>{item.refill_error_reason}</span>}
                      </div>
                    </Table.Td>
                  </>
                ) : (
                  <>
                    <Table.Td>
                      <Badge
                        color={item.type === ScanHistoryType.MANUAL ? "orange" : "blue"}
                        variant="light"
                        size="sm"
                      >
                        {sanitize(item.type ?? "auto")}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {/* One line per value this event set: status and/or sub-client. */}
                      {item.status_label || item.sub_client_name ? (
                        <div className="-mx-5 -my-4 flex flex-col divide-y divide-gray-300">
                          {item.status_label && (
                            <div className="flex w-full flex-wrap items-center gap-2 px-5 py-4">
                              <Badge color="gray" variant="outline" size="sm">
                                Status
                              </Badge>
                              <Badge
                                color={getItemStatusColor(item.status_label)}
                                variant="light"
                                size="md"
                              >
                                {sanitize(item.status_label)}
                              </Badge>
                            </div>
                          )}
                          {item.sub_client_name && (
                            <div className="flex w-full flex-wrap items-center gap-2 px-5 py-4">
                              <Badge color="gray" variant="outline" size="sm">
                                Sub-Client
                              </Badge>
                              <Badge color="grape" variant="light" size="md">
                                {item.sub_client_name}
                              </Badge>
                              {item.sub_client?.id && (
                                <Anchor
                                  size="xs"
                                  onClick={() =>
                                    window.open(`/sub-clients/${item.sub_client?.id}`, "_blank")
                                  }
                                  className="cursor-pointer"
                                  underline="always"
                                >
                                  View
                                </Anchor>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        "--"
                      )}
                    </Table.Td>
                    <Table.Td>
                      <div className="flex flex-col items-start gap-1">
                        <span>{item.location ?? "--"}</span>
                        {item.longitude && item.latitude && (
                          <Anchor
                            size="xs"
                            onClick={() =>
                              window.open(
                                `https://www.google.com/maps?q=${item.latitude},${item.longitude}`,
                                "_blank"
                              )
                            }
                            className="cursor-pointer"
                            underline="always"
                          >
                            View
                          </Anchor>
                        )}
                      </div>
                    </Table.Td>
                  </>
                )}
              </Table.Tr>
            );
          })}
        />
      </div>
      <ExportConfirmationModal
        opened={exportModalOpened}
        onClose={closeExportModal}
        onExportCurrentPage={handleExportCurrentPage}
        onExportAll={handleExportAll}
        currentPageCount={items.length}
        totalCount={total}
      />
    </div>
  );
};

export default HistoricalDataTable;
